import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User, AuthResponse, RegisterResponse } from '../types/auth';
import { authAPI } from '../services/api';
import { storage } from '../utils/storage';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        isAuthenticated: false,
        user: null,
      };
    case 'LOGOUT':
      return {
        ...initialState,
      };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (userNumber: string, loginCode: string) => Promise<void>;
  register: () => Promise<RegisterResponse>;
  recover: (userNumber: string, recoveryKeys: string[]) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initAuth = async () => {
      const token = storage.getToken();
      if (token) {
        try {
          dispatch({ type: 'AUTH_START' });
          const response = await authAPI.getMe();
          dispatch({ type: 'AUTH_SUCCESS', payload: response.data });
        } catch (error) {
          storage.clear();
          dispatch({ type: 'AUTH_ERROR', payload: 'Сессия истекла' });
        }
      }
    };

    initAuth();
  }, []);

  const login = async (userNumber: string, loginCode: string) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authAPI.login({ userNumber, loginCode });
      const { access_token, refresh_token, user } = response.data;

      storage.setToken(access_token);
      storage.setRefreshToken(refresh_token);
      storage.setUser(user);

      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Ошибка входа';
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const register = async (): Promise<RegisterResponse> => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authAPI.register();
      const { access_token, refresh_token, user } = response.data;

      storage.setToken(access_token);
      storage.setRefreshToken(refresh_token);
      storage.setUser(user);
      storage.setRegistrationData(response.data);

      dispatch({ type: 'AUTH_SUCCESS', payload: user });

      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Ошибка регистрации';
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const recover = async (userNumber: string, recoveryKeys: string[]) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authAPI.recover({ userNumber, recoveryKeys });
      const { access_token, refresh_token, user } = response.data;

      storage.setToken(access_token);
      storage.setRefreshToken(refresh_token);
      storage.setUser(user);

      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Ошибка восстановления';
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const logout = () => {
    storage.clear();
    storage.clearRegistrationData();
    storage.removeRefreshToken();
    dispatch({ type: 'LOGOUT' });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    recover,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
